import { API } from 'aws-amplify';

import { ActionTree, ActionContext } from 'vuex';
import { RootState } from '../types';
import { PropertiesState } from './types';

import { Job, Property } from '@/models';
import _ from 'lodash';
/**
 * Actions
 *
 * Actions are similar to mutations, the differences being that:
 * - Instead of mutating the state, actions commit mutations.
 * - Actions can contain arbitrary asynchronous operations.
 * - Actions are triggered with the store.dispatch method e.g. `store.dispatch('getSettings')`
 */
export const actions: ActionTree<PropertiesState, RootState> = {

	/**
	 * Get all Propertys
	 * 
	 * @param {PropertiesState} store
	 * @param filters
	 * @returns All Propertys
	 */
	async getProperties(store: ActionContext<PropertiesState, any>): Promise<Property[]> {
		store.commit('app/addRequest', 'getPropertys', { root: true });

		return API.get('RestAPI', `/properties`, {})
			.then((response) => response.map((x: Partial<Property>) => new Property(x)))
			.then((Property: Property[]) => {
				store.commit('setPropertys', Property);
				return Property;
			})
			.finally(() => store.commit('app/removeRequest', 'getPropertys', { root: true }));
	},

	/**
	 * Get one Property
	 * 
	 * @param {PropertiesState} store 
	 * @param id 
	 * @returns individual Property
	 */
	async getProperty(store: ActionContext<PropertiesState, any>, id) {
		// New Property
		if (id === 'new') {
			const property = new Property();
			store.commit('setCurrent', property);
			return property;
		}
		store.commit('app/addRequest', 'getProperty', { root: true });

		return API.get('RestAPI', `/properties/${id}`, null)
			.then((response) => new Property(response))
			.then((Property: Property) => {
				store.commit('setCurrent', Property);
				return Property;
			})
			.finally(() => store.commit('app/removeRequest', 'getProperty', { root: true }));
	},

	/**
   * Add Property
   * 
   * @param {PropertiesState} store 
   * @param Property Property
   */
	async addProperties(store: ActionContext<PropertiesState, any>, property: Property): Promise<void> {
		store.commit('app/addRequest', 'addProperty', { root: true });
		return API.post('RestAPI', '/properties', { body: property.toJSON() })
			.then((data) => new Property(data))
			.then((Propertyfromserver: Property) => {
				store.commit('resetUnsavedChanges');
			})
			.finally(() => store.commit('app/removeRequest', 'addProperty', { root: true }));
	},

	/**
   * Delete Property
   * 
   * @param {PropertiesState} store 
   * @param Property Property
   */
	async deleteProperties(store: ActionContext<PropertiesState, any>, Property: Property): Promise<void> {
		store.commit('app/addRequest', 'deleteProperty', { root: true });
		return API.del('RestAPI', `/properties/${Property.id}`, {})
			.then(() => {
				store.commit('resetUnsavedChanges');
				store.commit('setPropertys', _.remove(store.state.list, (c: Property) => c.id != Property.id));
			})
			.finally(() => store.commit('app/removeRequest', 'deleteProperty', { root: true }));
	},


	/**
   * Update given Property details
   * 
   * @param {PropertiesState} store 
   * @param Property Property
   */
	async updateProperties(store: ActionContext<PropertiesState, any>, property: Property): Promise<void> {
		store.commit('app/addRequest', 'updateProperty', { root: true });
		return API.put('RestAPI', `/properties/${property.id}`, { body: property.toJSON() })
			.then((data) => new Property(data))
			.then((Property: Property) => {
				store.commit('setCurrent', Property);
				store.commit('resetUnsavedChanges');
			})
			.finally(() => store.commit('app/removeRequest', 'updateProperty', { root: true }));
	},

	/**
   * Sets any deep Property property and increments the "unsaved changes" count
   * @param store
   * @param data
   */
	setPropertiesDeep(store: ActionContext<PropertiesState, any>, data): void {
		store.commit('setPropertiesDeep', data);
		store.commit('addUnsavedChange');
	},

	/**
 * Set the page for the Reports query
 *
 * @param store
 * @param data
 */
	resetCurrentPage(store: ActionContext<PropertiesState, any>): void {
		store.commit('setCurrentPage', 1);
	},


	/**
* Search getaddress for given postcode
* 
* @param {DiaryState} store 
* @param postcode: string 
* @returns response
*/

async getpropertiesaddress(store: ActionContext<PropertiesState, any>, postcode: string): Promise<Property[]> {
	const options = {
		response: true, // OPTIONAL (return the entire Axios response object instead of only response.data)
	};
	_.set(options, 'queryStringParameters.postcode', postcode);
	store.commit('app/addRequest', 'searchGetaddress', { root: true });

	return API.get('RestAPI', `/properties/search`, options)
		.then((res) => res.data.map((x: Partial<Property>) => new Property(x)))
		.then((property: Property[]) => {
			return property;
		})
		.finally(() => store.commit('app/removeRequest', 'searchGetaddress', { root: true }));
},
}
